import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
  type DocumentData,
} from "firebase/firestore";
import { db } from "@/firebaseConfig";
import type { Module, ActionContext } from "vuex";
import {
  ScheduleTemplate,
  ScheduleTemplateDoc,
  ScheduleTemplateElement,
} from "@/models/interfaces";
import { deepStripUndefined } from "@/utils/firestoreClean";
import { normalizeTemplateTitle } from "@/composables/useScheduleTemplates";
import { normalizeMobilePayNumber } from "@/utils/scheduleItemTypes";

export interface ScheduleTemplatesState {
  accountId: number | null;
  fetchedAccountId: number | null;
  items: ScheduleTemplate[];
  fetching: boolean;
  error: string | null;
}

type RootState = unknown;

// Sorted on the client: filtering on account and ordering by title together
// would need a composite index, and an organisation only has a handful.
const byTitle = (a: ScheduleTemplate, b: ScheduleTemplate) =>
  a.title.localeCompare(b.title, undefined, { sensitivity: "base" });

const cleanElements = (
  elements: ScheduleTemplateElement[],
): ScheduleTemplateElement[] =>
  elements.map((element) =>
    deepStripUndefined({
      type: element.type,
      title: element.title.trim(),
      description: element.description?.trim() || undefined,
      duration: Math.max(0, Math.round(element.duration)),
    }),
  );

/** Thrown when another template in the account already has the title. */
export class DuplicateTemplateTitleError extends Error {
  constructor(title: string) {
    super(`A schedule template named "${title}" already exists`);
    this.name = "DuplicateTemplateTitleError";
  }
}

const assertTitleAvailable = (
  state: ScheduleTemplatesState,
  title: string,
  exceptId?: string,
) => {
  const normalized = normalizeTemplateTitle(title);
  const taken = state.items.some(
    (item) =>
      item.id !== exceptId && normalizeTemplateTitle(item.title) === normalized,
  );
  if (taken) throw new DuplicateTemplateTitleError(title.trim());
};

const state = (): ScheduleTemplatesState => ({
  accountId: null,
  fetchedAccountId: null,
  items: [],
  fetching: false,
  error: null,
});

const mutations = {
  SET_ACCOUNT_ID(s: ScheduleTemplatesState, id: number | null) {
    s.accountId = id;
  },
  SET_FETCHED_ACCOUNT_ID(s: ScheduleTemplatesState, id: number | null) {
    s.fetchedAccountId = id;
  },
  SET_FETCHING(s: ScheduleTemplatesState, v: boolean) {
    s.fetching = v;
  },
  SET_ERROR(s: ScheduleTemplatesState, msg: string | null) {
    s.error = msg;
  },
  SET_ITEMS(s: ScheduleTemplatesState, items: ScheduleTemplate[]) {
    s.items = [...items].sort(byTitle);
  },
  UPSERT_ITEM(s: ScheduleTemplatesState, template: ScheduleTemplate) {
    s.items = [
      ...s.items.filter((item) => item.id !== template.id),
      template,
    ].sort(byTitle);
  },
  REMOVE_ITEM(s: ScheduleTemplatesState, id: string) {
    s.items = s.items.filter((item) => item.id !== id);
  },
};

const actions = {
  setAccountId(
    { state, commit }: ActionContext<ScheduleTemplatesState, RootState>,
    id: number | string | null | undefined,
  ) {
    // Some user docs store the account as a string.
    const accountId = id == null || id === "" ? null : Number(id);
    if (state.accountId !== accountId) {
      commit("SET_ACCOUNT_ID", accountId);
      commit("SET_FETCHED_ACCOUNT_ID", null);
      commit("SET_ITEMS", []);
    }
  },

  async fetchAll(
    { state, commit }: ActionContext<ScheduleTemplatesState, RootState>,
    options: { force?: boolean } = {},
  ) {
    if (state.accountId == null) {
      commit("SET_ITEMS", []);
      return;
    }
    if (
      !options.force &&
      (state.fetching || state.fetchedAccountId === state.accountId)
    )
      return;

    const accountId = state.accountId;
    commit("SET_FETCHING", true);
    commit("SET_ERROR", null);
    try {
      const snap = await getDocs(
        query(
          collection(db, "scheduleTemplates"),
          where("account", "==", accountId),
        ),
      );
      // The account may have changed while the query was in flight.
      if (state.accountId !== accountId) return;

      const templates: ScheduleTemplate[] = snap.docs.map((d) => {
        const data = d.data() as DocumentData;
        return {
          id: d.id,
          account: data.account as number,
          title: (data.title as string) ?? "",
          elements: Array.isArray(data.elements) ? data.elements : [],
          mobilePayNumber: (data.mobilePayNumber as string) ?? "",
          createdBy: (data.createdBy as string) ?? "",
          updatedAt: data.updatedAt,
        };
      });
      commit("SET_ITEMS", templates);
      commit("SET_FETCHED_ACCOUNT_ID", accountId);
    } catch (err: any) {
      commit("SET_ERROR", err?.message ?? String(err));
    } finally {
      commit("SET_FETCHING", false);
    }
  },

  async create(
    { state, commit }: ActionContext<ScheduleTemplatesState, RootState>,
    payload: {
      title: string;
      elements: ScheduleTemplateElement[];
      mobilePayNumber?: string;
      createdBy: string;
    },
  ): Promise<string> {
    if (state.accountId == null) throw new Error("No accountId set");
    assertTitleAvailable(state, payload.title);
    const docData: ScheduleTemplateDoc = {
      account: state.accountId,
      title: payload.title.trim(),
      elements: cleanElements(payload.elements),
      mobilePayNumber: normalizeMobilePayNumber(payload.mobilePayNumber),
      createdBy: payload.createdBy,
    };
    const ref = await addDoc(collection(db, "scheduleTemplates"), {
      ...docData,
      updatedAt: serverTimestamp(),
    });
    commit("UPSERT_ITEM", { id: ref.id, ...docData });
    return ref.id;
  },

  async update(
    { state, commit }: ActionContext<ScheduleTemplatesState, RootState>,
    payload: {
      id: string;
      title: string;
      elements: ScheduleTemplateElement[];
      mobilePayNumber?: string;
    },
  ) {
    const existing = state.items.find((item) => item.id === payload.id);
    if (!existing) throw new Error("Unknown schedule template");
    assertTitleAvailable(state, payload.title, payload.id);
    const changes = {
      title: payload.title.trim(),
      elements: cleanElements(payload.elements),
      mobilePayNumber: normalizeMobilePayNumber(payload.mobilePayNumber),
    };
    await updateDoc(doc(db, "scheduleTemplates", payload.id), {
      ...changes,
      updatedAt: serverTimestamp(),
    });
    commit("UPSERT_ITEM", { ...existing, ...changes });
  },

  async remove(
    { commit }: ActionContext<ScheduleTemplatesState, RootState>,
    id: string,
  ) {
    await deleteDoc(doc(db, "scheduleTemplates", id));
    commit("REMOVE_ITEM", id);
  },
};

const getters = {
  templates(state: ScheduleTemplatesState): ScheduleTemplate[] {
    return state.items;
  },
  hasFetched(state: ScheduleTemplatesState): boolean {
    return (
      state.accountId != null && state.fetchedAccountId === state.accountId
    );
  },
  isFetching(state: ScheduleTemplatesState): boolean {
    return state.fetching;
  },
  error(state: ScheduleTemplatesState): string | null {
    return state.error;
  },
};

export const scheduleTemplates: Module<ScheduleTemplatesState, RootState> = {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
export default scheduleTemplates;
